import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import {
  AdminOverview,
  getAdminOverview,
  RecognitionMetrics,
} from "../../../Services/Admin/Overview";
import SpotifyLabelPanel from "../SpotifyLabelPanel/SpotifyLabelPanel";
import "./RecognitionPanel.css";

const METRICS: Array<[keyof RecognitionMetrics, string, string]> = [
  ["auddCalls", "AudD calls", "Requests sent to the external recognition service"],
  ["localHits", "Local fingerprint hits", "Accepted matches served from the local cache"],
  ["localMisses", "Local misses", "No stored fingerprint was close enough"],
  ["localRejected", "Local rejections", "Ambiguous candidates rejected for safety"],
  ["windowSuppressed", "Window suppressed", "Requests avoided while the current track was still playing"],
  ["spotifyLabelled", "Spotify labelled", "Recognitions labelled from the Spotify timeline"],
  ["fingerprintErrors", "Fingerprint errors", "Fingerprint processing failures"],
];

export default function RecognitionPanel() {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getAdminOverview()
      .then(setData)
      .catch((e) => setError(e?.message ?? "Could not load recognition metrics"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <div className="recognition-admin-grid">
      <section className="admin-surface">
        <div className="admin-section-heading">
          <div>
            <span className="admin-eyebrow">Last 7 days</span>
            <h2>Recognition health</h2>
          </div>
          <button className="admin-icon-button" type="button" onClick={load} disabled={loading} aria-label="Refresh recognition metrics">
            <RefreshCw size={16} className={loading ? "is-spinning" : ""} />
          </button>
        </div>
        {error && <div className="admin-error"><AlertTriangle size={15} /> {error}</div>}
        {!data && loading ? (
          <div className="recognition-metric-grid recognition-metric-grid--loading" />
        ) : data ? (
          <div className="recognition-metric-grid">
            {METRICS.map(([key, label, help]) => (
              <div key={key}>
                <span>{label}</span>
                <strong>{data.recognition.last7Days[key].toLocaleString()}</strong>
                <small>{help}</small>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="admin-surface">
        <div className="admin-section-heading">
          <div>
            <span className="admin-eyebrow">Labelling tool</span>
            <h2>Spotify labelling</h2>
          </div>
        </div>
        <SpotifyLabelPanel />
      </section>
    </div>
  );
}
